// @givmo/app-charity — the back-office setup landing (CBO-IN3, surface 1).
//
// ══ WHAT THIS SCREEN IS ════════════════════════════════════════════════════════════════════════
//
// One question, answered from the organization's own records: does it have a back-office record yet,
// and what is that record waiting on? Everything a person can DO to a record lives on the record's
// own page; this one resolves which record that is, or offers to start one.
//
// ⚠ IT NEVER CLAIMS TO SHOW EVERY RECORD. It reads the first page of a newest-first listing, and the
// live record is on it by construction (the backend refuses a second live one). The history beneath
// is shown as "recent", never as "all".
//
// ══ THE GATES, IN ORDER ════════════════════════════════════════════════════════════════════════
//
//   1. ORG — fail closed. Nothing reads and nothing renders until the ACTIVE org is positively
//      resolved (`isReady`). A pending or errored membership read gets a skeleton or a retry, never
//      an enrollment read fired against an empty charity id.
//   2. MODULE — a 404 from either gate means the module is dark in this environment, and the honest
//      rendering says so rather than "something went wrong". The two dark states are byte-identical
//      to a record that does not exist, on purpose.
//   3. ROLE — starting a record is an Owner act (the backend scopes `charity_enrollment:write` to
//      Owners, beside terms acceptance and payment writes). A Member READS everything here and is
//      offered no control the backend would refuse.
//
// The root keeps `data-testid="page-enrollment"` — the data contract e2e and the guard tests assert.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  ErrorMessage,
  Icon,
  PageHeader,
  Skeleton,
  testIdProp,
} from '@givmo/design-system';
import { ArrowRight, Building2, ShieldAlert } from 'lucide-react';
import { isOwnerRole } from '../admins/admins-roles';
import { useActiveOrg } from '../../org/active-org-context';
import {
  displayEnrollmentDate,
  extractEnrollments,
  findLiveEnrollment,
  isAlreadyEnrolled,
  isModuleUnavailable,
  readEnrollmentDetail,
  serverMessage,
  type EnrollmentRecord,
} from './enrollment-decode';
import { createEnrollmentDraft, listEnrollments } from './enrollment-transport';
import { ENROLLMENT_COPY, enrollmentStatusVocab } from './enrollment-vocab';
import { ENROLLMENT_ROUTES } from './enrollment-routes';

const PREFIX = 'enrollment';

/** The listing's cache key. Hand-written, so it carries no `_id` and no generated family matches it. */
export function enrollmentListKey(charityId: string | null): readonly unknown[] {
  return ['enrollment', 'list', charityId];
}

export function EnrollmentPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { activeMembership, activeCharityId, isReady, isPending, isError, refetch } =
    useActiveOrg();

  const isOwner = activeMembership !== null && isOwnerRole(activeMembership.role);

  const listQuery = useQuery({
    queryKey: enrollmentListKey(activeCharityId),
    queryFn: () => listEnrollments(activeCharityId ?? ''),
    select: extractEnrollments,
    enabled: isReady && activeCharityId !== null,
  });

  const startMutation = useMutation({
    mutationFn: () => createEnrollmentDraft(activeCharityId ?? ''),
    onSuccess: (body) => {
      void queryClient.invalidateQueries({ queryKey: enrollmentListKey(activeCharityId) });
      // Navigate to the record the backend actually created rather than to a refetched listing:
      // the create response IS the record, so reading its id here avoids a round trip that could
      // land on a stale page.
      const created = readEnrollmentDetail(body);
      if (created !== null) {
        void navigate({ to: ENROLLMENT_ROUTES.record(created.id) });
      }
    },
    // ⚠ A 409 means the listing this screen is rendering is STALE — it showed no live record, or the
    // start affordance would not have been offered. The refusal copy tells the person to open the
    // record that already exists, so the record has to actually be on the page; without this refetch
    // it is not there until a reload, and the screen asks for something it has just made impossible.
    onError: (error) => {
      if (isAlreadyEnrolled(error)) {
        void queryClient.invalidateQueries({ queryKey: enrollmentListKey(activeCharityId) });
      }
    },
  });

  // ── Org gate — fail closed ───────────────────────────────────────────────────────────────────────
  if (isPending) {
    return (
      <PageShell>
        <Card className="p-24" testId={`${PREFIX}-loading`}>
          <div className="flex flex-col gap-10">
            <Skeleton className="h-16 w-1/3" />
            <Skeleton className="h-12 w-2/3" />
          </div>
        </Card>
      </PageShell>
    );
  }

  if (isError || !isReady) {
    return (
      <PageShell>
        <div className="rounded-xl border border-border-subtle bg-surface">
          <EmptyState
            testId={`${PREFIX}-org-error`}
            icon={<Icon icon={ShieldAlert} size={20} aria-hidden />}
            title="Couldn’t load your organization"
            description="We couldn’t load your organization. Please try again."
            action={
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={refetch}
                testId={`${PREFIX}-org-retry`}
              >
                Try again
              </Button>
            }
          />
        </div>
      </PageShell>
    );
  }

  // ── Module gate — a dark module is not an error ──────────────────────────────────────────────────
  if (listQuery.isError && isModuleUnavailable(listQuery.error)) {
    return (
      <PageShell>
        <div className="rounded-xl border border-border-subtle bg-surface">
          <EmptyState
            testId={`${PREFIX}-unavailable`}
            icon={<Icon icon={Building2} size={20} aria-hidden />}
            title={ENROLLMENT_COPY.unavailableTitle}
            description={ENROLLMENT_COPY.unavailableBody}
          />
        </div>
      </PageShell>
    );
  }

  if (listQuery.isPending) {
    return (
      <PageShell>
        <Card className="p-24" testId={`${PREFIX}-list-loading`}>
          <Skeleton className="h-16 w-1/2" />
        </Card>
      </PageShell>
    );
  }

  if (listQuery.isError) {
    return (
      <PageShell>
        <div className="rounded-xl border border-border-subtle bg-surface">
          <EmptyState
            testId={`${PREFIX}-error`}
            icon={<Icon icon={ShieldAlert} size={20} aria-hidden />}
            title="Couldn’t load your setup"
            description={serverMessage(
              listQuery.error,
              'We couldn’t load your organization’s setup. Please try again.',
            )}
            action={
              <Button
                appearance="saas"
                tone="secondary"
                size="sm"
                onClick={() => void listQuery.refetch()}
                testId={`${PREFIX}-retry`}
              >
                Try again
              </Button>
            }
          />
        </div>
      </PageShell>
    );
  }

  const records = listQuery.data;
  const live = findLiveEnrollment(records);
  const history = records.filter((record) => record.id !== live?.id);

  return (
    <PageShell>
      {live === null ? (
        <Card testId={`${PREFIX}-start`}>
          <CardBody>
            <div className="flex flex-col gap-16">
              <div className="flex flex-col gap-8">
                <h2 className="font-sans text-s18 font-w600 text-neutral-900">
                  {ENROLLMENT_COPY.startTitle}
                </h2>
                <p className="text-s14 text-neutral-700">{ENROLLMENT_COPY.startBody}</p>
              </div>
              {/* OWNER GATE — fail closed. A Member sees the explanation and no control, matching what
                  the backend would allow rather than offering a button that 403s. */}
              {isOwner ? (
                <div className="flex flex-col gap-8">
                  <div>
                    <Button
                      appearance="saas"
                      tone="primary"
                      size="md"
                      onClick={() => startMutation.mutate()}
                      disabled={startMutation.isPending}
                      testId={`${PREFIX}-start-action`}
                    >
                      {ENROLLMENT_COPY.startAction}
                    </Button>
                  </div>
                  {startMutation.isError && (
                    <ErrorMessage testId={`${PREFIX}-start-error`}>
                      {isAlreadyEnrolled(startMutation.error)
                        ? ENROLLMENT_COPY.alreadyEnrolledBody
                        : serverMessage(
                            startMutation.error,
                            'We couldn’t start the setup. Please try again.',
                          )}
                    </ErrorMessage>
                  )}
                </div>
              ) : (
                <p className="text-s12 text-neutral-600" {...testIdProp(`${PREFIX}-owner-only`)}>
                  An Owner of your organization can start this.
                </p>
              )}
            </div>
          </CardBody>
        </Card>
      ) : (
        <RecordSummary record={live} testId={`${PREFIX}-live`} onOpen={navigate} />
      )}

      {history.length > 0 && (
        <div className="flex flex-col gap-12" {...testIdProp(`${PREFIX}-history`)}>
          <h2 className="font-sans text-s14 font-w600 text-neutral-900">Recent records</h2>
          {history.map((record) => (
            <RecordSummary
              key={record.id}
              record={record}
              testId={`${PREFIX}-history-${record.id}`}
              onOpen={navigate}
            />
          ))}
        </div>
      )}
    </PageShell>
  );
}

/** One record, summarized: what it is called, where it stands, and a way into it. */
function RecordSummary({
  record,
  testId,
  onOpen,
}: {
  record: EnrollmentRecord;
  testId: string;
  onOpen: ReturnType<typeof useNavigate>;
}) {
  const vocab = enrollmentStatusVocab(record.status);
  const started = displayEnrollmentDate(record.createdAt);

  return (
    <Card testId={testId}>
      <CardBody>
        <div className="flex flex-col gap-12">
          <div className="flex flex-wrap items-center gap-8">
            <Badge tone={vocab.tone} testId={`${testId}-status`}>
              {vocab.label}
            </Badge>
            {/* An unparseable or absent date renders NOTHING rather than a date-shaped blank. */}
            {started.length > 0 && (
              <span className="text-s12 text-neutral-600">Started {started}</span>
            )}
          </div>
          {record.legalEntityName !== null && (
            <span
              className="font-sans text-s16 font-w600 text-neutral-900"
              {...testIdProp(`${testId}-name`)}
            >
              {record.legalEntityName}
            </span>
          )}
          <p className="text-s14 text-neutral-700">{vocab.description}</p>
          <div>
            <Button
              appearance="saas"
              tone="secondary"
              size="sm"
              onClick={() => void onOpen({ to: ENROLLMENT_ROUTES.record(record.id) })}
              testId={`${testId}-open`}
            >
              Open record
              <Icon icon={ArrowRight} size={16} aria-hidden />
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mx-auto flex w-full max-w-[840px] flex-col gap-24 px-24 py-24"
      {...testIdProp('page-enrollment')}
    >
      <PageHeader title={ENROLLMENT_COPY.pageTitle} subtitle={ENROLLMENT_COPY.pageSubtitle} />
      {children}
    </div>
  );
}
